import { Criterion, Project, ScheduleItem, EventSettings, GrantPrizeTier } from '../types';

export const DEFAULT_CRITERIA: Criterion[] = [
  {
    id: 'c1',
    name: 'Innovative',
    question: 'Is it a creative and/or unique approach to ministry?',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  },
  {
    id: 'c2',
    name: 'Impacting',
    question: 'Are there measurable and reproducible results?',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  },
  {
    id: 'c3',
    name: 'Sustainable',
    question: 'Does it have the infrastructure to support its staff and ministry?',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  },
  {
    id: 'c4',
    name: 'Scalable',
    question: 'Is there a reproducible model to support exponential growth?',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  },
  {
    id: 'c5',
    name: 'Diverse',
    question: 'Does this represent diversity of age, gender, ethnicity?',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  },
  {
    id: 'c6',
    name: 'Transferable',
    question: 'Is it possible to replicate these practices in the church?',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  },
  {
    id: 'c7',
    name: 'New',
    question: 'Is this ministry 6 months to 3 years old?',
    lowLabel: '1 Below average',
    midLabel: '3 Average',
    highLabel: '5 Amazing',
  },
];

export const DEFAULT_PRIZE_TIERS: GrantPrizeTier[] = [
  { place: '1st Place', amount: '$25,000', rank: 1 },
  { place: '2nd Place', amount: '$20,000', rank: 2 },
  { place: '3rd Place', amount: '$15,000', rank: 3 },
  { place: '4th Place', amount: '$10,000', rank: 4 },
  { place: '5th Place', amount: '$5,000', rank: 5 },
];

export const DEFAULT_PROJECTS: Project[] = [
  {
    id: 'p1',
    order: 1,
    title: 'Break Free Ministries',
    presenter: 'Michael Swalley',
    website: 'https://breakfreeministries.org',
    purpose: 'To liberate individuals trapped in addiction, trauma, and cycles of incarceration through Christ-centered recovery and community re-entry.',
    bigIdea: 'A holistic discipleship model combining trauma-informed peer counseling, vocational trade mentorship, and local church integration to break generational bondage.',
    missionField: 'Young adults and families wrestling with substance abuse and justice system involvement in underserved communities.',
    goals: 'Establish 4 regional recovery hubs, train 50 church-based recovery facilitators, and achieve an 85% sober transition rate across cohorts.',
    results: 'Over 140 individuals walked through recovery cohorts with a 78% retention rate and 45 baptisms in partner churches over the past 18 months.',
    categoryTags: ['Recovery & Mercy', 'Community Outreach', 'Discipleship'],
  },
  {
    id: 'p2',
    order: 2,
    title: 'Front Porch Church',
    presenter: 'Rick Bolle',
    website: 'https://frontporchchurch.com',
    purpose: 'To cultivate hyper-local neighborhood micro-churches centered around front yard hospitality and genuine neighbor love.',
    bigIdea: 'Transforming suburban front porches and driveways into spiritual living rooms where unchurched neighbors gather naturally for table fellowship, prayer, and scripture.',
    missionField: 'Suburban and exurban neighborhoods experiencing acute isolation, commuter disconnection, and skepticism of institutional church buildings.',
    goals: 'Plant and coach 25 front-porch micro-churches across 5 metro areas, training ordinary homeowners as neighborhood pastors.',
    results: 'Currently 8 active neighborhood front-porch fellowships meeting weekly, with 60% of regular attendees previously disconnected from church.',
    categoryTags: ['Microchurch', 'Church Planting', 'Hospitality & Evangelism'],
  },
  {
    id: 'p3',
    order: 3,
    title: 'Discipology',
    presenter: 'Peyton Jones and Kris Langham',
    website: 'https://discipology.org',
    purpose: 'To demystify disciple-making through rapid, mobile-first audio guides that empower everyday believers to lead others to Christ.',
    bigIdea: 'An intuitive, habit-forming audio platform pairing snackable scripture commentary with actionable daily discipleship prompts for friends, coworkers, and small groups.',
    missionField: 'Everyday Christians who feel untrained or intimidated by traditional discipleship materials, reaching digital-native seekers.',
    goals: 'Release complete New Testament audio tracks, equip 1,000 church small-group leaders, and scale active weekly listeners to 50,000+.',
    results: 'Over 22,000 app downloads across 14 countries, with 3,400 active disciple-making pairs walking through guided scripture journeys.',
    categoryTags: ['Digital & Media', 'Discipleship', 'Evangelism'],
  },
  {
    id: 'p4',
    order: 4,
    title: 'Grace Bomb',
    presenter: 'Pat Linnell',
    website: 'https://gracebomb.org',
    purpose: 'To awaken a culture of spontaneous, generous, everyday neighbor love that visibly points people to Jesus.',
    bigIdea: 'A movement equipping Christians to drop unexpected, tactical acts of biblical generosity ("Grace Bombs") accompanied by a card pointing credit back to God.',
    missionField: 'Everyday interactions in coffee shops, grocery stores, gas stations, schools, and workplaces where people feel cynical or unvalued.',
    goals: 'Equip 500 churches nationwide to launch congregation-wide Grace Bomb campaigns, unleashing 250,000 individual acts of generosity.',
    results: 'Over 85,000 Grace Bombs documented across 40 states and 8 countries, resulting in hundreds of recorded gospel conversations and testimonies.',
    categoryTags: ['Evangelism', 'Generosity', 'Church Culture'],
  },
  {
    id: 'p5',
    order: 5,
    title: 'Kaleo AI',
    presenter: 'Aidan Britnell',
    website: 'https://kaleo.ai',
    purpose: 'To empower pastors and church planters with intelligent pastoral sermon research, sermon-to-discipleship workflows, and multilingual translation.',
    bigIdea: 'A secure, ethically trained theological AI assistant built specifically for ministry leaders to multiply their administrative capacity and deepen personal discipleship.',
    missionField: 'Time-constrained bi-vocational pastors, solo church planters, and multi-ethnic congregations needing localized ministry resources.',
    goals: 'Support 5,000 bi-vocational pastors, roll out real-time audio translation in 12 languages, and save pastors an average of 8 hours weekly.',
    results: '750 beta pastors using Kaleo across 18 denominations, generating over 10,000 sermon-aligned discipleship study guides for weekly small groups.',
    categoryTags: ['Tech & AI', 'Church Systems', 'Pastoral Leadership'],
  },
  {
    id: 'p6',
    order: 6,
    title: 'OneChurch.io',
    presenter: 'James Donley',
    website: 'https://onechurch.io',
    purpose: 'To connect regional churches into collaborative city networks that share resources, volunteer pools, and unified compassion responses.',
    bigIdea: 'A unified city-reaching data and resource collaboration platform breaking down church competition and enabling shared gospel impact.',
    missionField: 'City-wide ministry networks, pastoral fraternals, and community relief coalitions looking to pool kingdom assets.',
    goals: 'Deploy collaborative network hubs in 10 major US cities, onboarding 300+ cooperating churches onto the shared community need grid.',
    results: 'Piloted across 3 metropolitan counties, routing over $450,000 in shared benevolence resources and mobilizing 1,200 cross-church volunteers.',
    categoryTags: ['Tech & AI', 'City Networks', 'Compassion & Relief'],
  },
  {
    id: 'p7',
    order: 7,
    title: 'Seeds of Change Network',
    presenter: 'Doug Beutler',
    website: 'https://seedsofchangenetwork.org',
    purpose: 'To revitalize declining rural and small-town churches through micro-enterprise incubators and collaborative bi-vocational ministry.',
    bigIdea: 'Transforming rural church properties into community innovation hubs (shared farming, commercial kitchens, maker spaces) that fund rural pastors and foster local fellowship.',
    missionField: 'Rural communities facing population decline, economic stagnation, and closing small-town sanctuaries.',
    goals: 'Revitalize 20 rural sanctuaries into self-sustaining kingdom enterprise hubs across 4 agricultural states over the next 24 months.',
    results: '5 rural demonstration church hubs operating with 100% financial sustainability, generating 14 local jobs and revitalizing Sunday worship.',
    categoryTags: ['Rural Ministry', 'Church Planting', 'Community Development'],
  },
  {
    id: 'p8',
    order: 8,
    title: 'MicroChurch Residency',
    presenter: 'Rodrigo Cano',
    website: 'https://microchurchresidency.com',
    purpose: 'To discover, train, and send next-generation bi-vocational leaders to launch autonomous gospel micro-churches where they work and live.',
    bigIdea: 'An intensive 9-month immersive cohort equipping ordinary believers with theological depth, pastoral care skills, and missional agility without requiring seminary debt.',
    missionField: 'Young leaders, college students, and workplace professionals eager to plant churches in untraditional spaces like gyms, pubs, and campuses.',
    goals: 'Graduate 100 micro-church planters per year and establish decentralized planting networks in 15 target metro areas.',
    results: 'Graduated 42 residents over 2 cohorts who have successfully launched 31 thriving micro-churches currently reaching 650+ believers and seekers.',
    categoryTags: ['Leadership & Residency', 'Microchurch', 'NextGen'],
  },
  {
    id: 'p9',
    order: 9,
    title: 'MyBLVD',
    presenter: 'Dhati Lewis',
    website: 'https://myblvd.org',
    purpose: 'To empower indigenous leaders to establish holistic, disciple-making churches in urban and historically neglected communities.',
    bigIdea: 'A movement-building ecosystem that equips urban leaders to plant and sustain life-giving churches that address spiritual, economic, and systemic brokenness.',
    missionField: 'Urban density neighborhoods, majority-minority zip codes, and urban blvds seeking indigenous discipleship and lasting gospel stability.',
    goals: 'Coach 50 urban church planter teams, produce culturally contextual discipleship resources, and seed 10 community development corporations.',
    results: 'Supported 35 urban church plants with an 88% 5-year survival rate and engaged 1,500+ leaders through regional BLVD summits.',
    categoryTags: ['Urban Ministry', 'Diversity & Equity', 'Church Planting'],
  },
  {
    id: 'p10',
    order: 10,
    title: 'Zebulun Initiative',
    presenter: 'Dino Rosato',
    website: 'https://zebuluninitiative.org',
    purpose: 'To mobilize Christian entrepreneurs, marketplace leaders, and trade craftspeople for frontier kingdom advancement and missional enterprise.',
    bigIdea: 'Creating kingdom-driven businesses in under-reached markets that generate fair-wage jobs, mentor young apprentices, and establish organic church plants.',
    missionField: 'Marketplaces, trade corridors, and global frontier regions where traditional foreign mission approaches are restricted or ineffective.',
    goals: 'Launch 15 missional businesses, create 200 gospel-centered workplace jobs, and embed discipleship teams in 5 closed-access regions.',
    results: '6 operating missional businesses generating $1.8M in ethical revenue and supporting 4 church-planting teams in unreached areas.',
    categoryTags: ['Marketplace Ministry', 'Innovation', 'Global Missions'],
  },
  {
    id: 'p11',
    order: 11,
    title: 'PoolChurch',
    presenter: 'Michael Hewitt',
    website: 'https://poolchurch.org',
    purpose: 'To reach unchurched families and youth through community aquatic sports, swim discipleship, and pool-side gatherings.',
    bigIdea: 'Repurposing municipal and residential pools into spaces of authentic community, water-safety education for underprivileged children, and casual relational ministry.',
    missionField: 'Families, youth, and children in urban and warm-climate communities who would never step foot inside a conventional church building.',
    goals: 'Train 30 swim-coach ministry leaders, provide free drowning prevention lessons to 1,000 inner-city youth, and plant 12 pool-side communities.',
    results: 'Over 650 kids certified in water safety with 4 active pool-side worship communities meeting weekly and 38 family baptisms held right in the pool.',
    categoryTags: ['Creative Ministry', 'NextGen & Youth', 'Evangelism'],
  },
  {
    id: 'p12',
    order: 12,
    title: 'Kamp Love',
    presenter: 'Austin Rockwell',
    website: 'https://kamplove.org',
    purpose: 'To provide life-changing summer camp retreats and year-round mentorship for children in foster care and kinship placements.',
    bigIdea: 'High-energy, trauma-informed adventure camps that pair every foster child one-on-one with a trained Christian mentor for an unforgettable week of dignity and love.',
    missionField: 'Children and teens navigating the foster care system, group homes, and relational heartbreak.',
    goals: 'Host 10 regional camp weeks serving 500 foster children, pairing each child with a year-round church-based mentor.',
    results: 'Served 280 foster youth across 4 regional camps with a 100% volunteer return rate and over 80 youth connected to permanent church mentor families.',
    categoryTags: ['Compassion & Mercy', 'NextGen & Youth', 'Foster Care'],
  },
];

export const DEFAULT_SCHEDULE: ScheduleItem[] = [
  // DAY ONE: Tuesday, September 29, 2026
  {
    id: 's1',
    date: 'Tuesday, September 29, 2026',
    time: '12:00-1:00 p.m.',
    title: 'Registration and Networking',
    description: 'Check in, receive attendee materials, and connect with other leaders and ministry founders.',
    speakerOrLeader: 'Hospitality Team',
    category: 'general',
  },
  {
    id: 's2',
    date: 'Tuesday, September 29, 2026',
    time: '1:00 p.m.',
    title: 'Ministry Presentations',
    description: 'Main-stage ministry showcase pitches and introductions from the first cohort of ministry founders.',
    speakerOrLeader: 'Main Auditorium',
    category: 'pitch',
  },
  {
    id: 's3',
    date: 'Tuesday, September 29, 2026',
    time: '1:50 p.m.',
    title: 'Break',
    description: 'Short transition break.',
    category: 'break',
  },
  {
    id: 's4',
    date: 'Tuesday, September 29, 2026',
    time: '2:00 p.m.',
    title: 'Ministry Presentations',
    description: 'Main-stage ministry presentations continue.',
    speakerOrLeader: 'Main Auditorium',
    category: 'pitch',
  },
  {
    id: 's5',
    date: 'Tuesday, September 29, 2026',
    time: '2:35 p.m.',
    title: 'Break',
    description: 'Transition to Breakout Session 1 rooms across the venue.',
    category: 'break',
  },
  {
    id: 's6',
    date: 'Tuesday, September 29, 2026',
    time: '2:50 p.m.',
    title: 'Breakout Session 1',
    description: 'Join a ministry breakout room for in-depth discussion:\n• Break Free Ministries - Michael Swalley | Auditorium A\n• Front Porch Church - Rick Boile | Auditorium B\n• Discipology - Peyton Jones/Kris Langham | Auditorium C\n• Grace Bomb - Pat Linnell | The Gathering Room\n• Kaleo AI - Aidan Britnell | SP Lounge\n• OneChurch.io - James Donley | Prayer Room',
    speakerOrLeader: 'Cohort 1 Presenters',
    category: 'pitch',
  },
  {
    id: 's7',
    date: 'Tuesday, September 29, 2026',
    time: '3:30 p.m.',
    title: 'Break',
    description: 'Transition to Breakout Session 2 rooms.',
    category: 'break',
  },
  {
    id: 's8',
    date: 'Tuesday, September 29, 2026',
    time: '3:45 p.m.',
    title: 'Breakout Session 2',
    description: 'Join your second ministry breakout room for Q&A:\n• Break Free Ministries - Michael Swalley | Auditorium A\n• Front Porch Church - Rick Boile | Auditorium B\n• Discipology - Peyton Jones/Kris Langham | Auditorium C\n• Grace Bomb - Pat Linnell | The Gathering Room\n• Kaleo AI - Aidan Britnell | SP Lounge\n• OneChurch.io - James Donley | Prayer Room',
    speakerOrLeader: 'Cohort 1 Presenters',
    category: 'pitch',
  },
  {
    id: 's9',
    date: 'Tuesday, September 29, 2026',
    time: '4:25 p.m.',
    title: 'Break / Head to Auditorium A',
    description: 'Regather with all attendees in Auditorium A for Day 1 closing.',
    speakerOrLeader: 'Auditorium A',
    category: 'break',
  },
  {
    id: 's10',
    date: 'Tuesday, September 29, 2026',
    time: '4:40 p.m.',
    title: 'Closing Day 1',
    description: 'Closing reflections, prayer, and preview of Wednesday morning presentations.',
    speakerOrLeader: 'Event Emcee',
    category: 'general',
  },

  // DAY TWO: Wednesday, September 30, 2026
  {
    id: 's11',
    date: 'Wednesday, September 30, 2026',
    time: '8:30 a.m.',
    title: 'Doors Open / Networking',
    description: 'Morning fellowship, check-in, and networking before main-stage presentations begin.',
    speakerOrLeader: 'Hospitality Team',
    category: 'general',
  },
  {
    id: 's12',
    date: 'Wednesday, September 30, 2026',
    time: '9:00 a.m.',
    title: 'Ministry Presentations',
    description: 'Main-stage ministry showcase pitches and introductions from the second cohort of ministry founders.',
    speakerOrLeader: 'Main Auditorium',
    category: 'pitch',
  },
  {
    id: 's13',
    date: 'Wednesday, September 30, 2026',
    time: '9:35 a.m.',
    title: 'Break',
    description: 'Short transition break.',
    category: 'break',
  },
  {
    id: 's14',
    date: 'Wednesday, September 30, 2026',
    time: '9:40 a.m.',
    title: 'Ministry Presentations',
    description: 'Main-stage ministry presentations continue.',
    speakerOrLeader: 'Main Auditorium',
    category: 'pitch',
  },
  {
    id: 's15',
    date: 'Wednesday, September 30, 2026',
    time: '10:15 a.m.',
    title: 'Break',
    description: 'Transition to Breakout Session 3 rooms across the venue.',
    category: 'break',
  },
  {
    id: 's16',
    date: 'Wednesday, September 30, 2026',
    time: '10:30 a.m.',
    title: 'Breakout Session 3',
    description: 'Join a ministry breakout room for in-depth discussion:\n• Kamp Love - Austin Rockwell | Auditorium A\n• PoolChurch - Michael Hewitt | Auditorium B\n• MyBLVD - Dhati Lewis | Auditorium C\n• Seeds of Change Network - Doug Beutler | The Gathering Room\n• MicroChurch Residency - Rodrigo Cano | SP Lounge\n• Zebulun Initiative - Dino Rosato | Prayer Room',
    speakerOrLeader: 'Cohort 2 Presenters',
    category: 'pitch',
  },
  {
    id: 's17',
    date: 'Wednesday, September 30, 2026',
    time: '11:10 a.m.',
    title: 'Break',
    description: 'Transition to Breakout Session 4 rooms.',
    category: 'break',
  },
  {
    id: 's18',
    date: 'Wednesday, September 30, 2026',
    time: '11:25 a.m.',
    title: 'Breakout Session 4',
    description: 'Join your fourth ministry breakout room for Q&A:\n• Kamp Love - Austin Rockwell | Auditorium A\n• PoolChurch - Michael Hewitt | Auditorium B\n• MyBLVD - Dhati Lewis | Auditorium C\n• Seeds of Change Network - Doug Beutler | The Gathering Room\n• MicroChurch Residency - Rodrigo Cano | SP Lounge\n• Zebulun Initiative - Dino Rosato | Prayer Room',
    speakerOrLeader: 'Cohort 2 Presenters',
    category: 'pitch',
  },
  {
    id: 's19',
    date: 'Wednesday, September 30, 2026',
    time: '12:05 p.m.',
    title: 'Lunch',
    description: 'Enjoy lunch with fellow attendees, discuss impressions, and prepare for voting.',
    speakerOrLeader: 'All Attendees',
    category: 'break',
  },
  {
    id: 's20',
    date: 'Wednesday, September 30, 2026',
    time: '1:00 p.m.',
    title: 'Voting',
    description: 'Official judges voting window opens. All judges rate the 12 ministries on the 1–5 scale across all 7 criteria.',
    speakerOrLeader: 'NEXT Ventures Judges',
    category: 'voting',
  },
  {
    id: 's21',
    date: 'Wednesday, September 30, 2026',
    time: '1:15 p.m.',
    title: 'Special Guest Presentation',
    description: 'Special guest presentation and keynote address while ballots are audited and tallies compiled.',
    speakerOrLeader: 'Keynote Speaker',
    category: 'general',
  },
  {
    id: 's22',
    date: 'Wednesday, September 30, 2026',
    time: '1:35 p.m.',
    title: 'Grant Winners Declared',
    description: 'Live grant announcements: $25k, $20k, $15k, $10k, and $5k grants presented to the Top 5 finalists!',
    speakerOrLeader: 'NEXT Ventures Leadership',
    category: 'awards',
  },
  {
    id: 's23',
    date: 'Wednesday, September 30, 2026',
    time: '1:50 p.m.',
    title: 'Ventures Closing',
    description: 'Commissioning prayer and closing celebration for all 12 kingdom innovation ministries.',
    speakerOrLeader: 'Event Leadership',
    category: 'general',
  },
];

export const DEFAULT_SETTINGS: EventSettings = {
  eventName: 'NEXT Ventures Shark Tank 2026',
  subtitle: 'Judges Voting & Pitch Showcase for Kingdom Innovation Grants',
  eventDate: 'September 29–30, 2026',
  venue: 'Main Auditorium & Ministry Breakout Rooms',
  votingBlurb: `NEXT Ventures Shark Tank 2026 Judges! It's time to choose your top picks for our grant finalists.

1st Place $25,000
2nd Place $20,000
3rd Place $15,000
4th Place $10,000
5th Place $5,000

Please judge the ministries using our criteria areas on a scale of 1-5.

• 5 amazing
• 3 average
• 1 below average

Using your ratings for each ministry and each area below, we will compile the voting from NEXT Ventures Shark Tank 2026 Judges with 1st place going to the highest score, etc.

Thank you for your support of NEXT Ventures and for your participation in this vote!`,
  prizeTiers: DEFAULT_PRIZE_TIERS,
  phase: 'VOTING_OPEN',
  revealStep: 0,
  showLiveScoresToAudience: false,
  adminPin: '1616',
  staffVoteWeight: 3.0,
  staffPasscode: '2026',
  programBookUrl: 'https://www.flipsnack.com/multiplication/2026-shark-tank-program',
};
